import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

interface ProgressContextType {
  start:      (label?: string) => void;  // barra top
  done:       () => void;
  showLoader: (label?: string) => void;  // overlay fullscreen
  hideLoader: () => void;
}

const ProgressContext = createContext<ProgressContextType>({
  start:      () => {},
  done:       () => {},
  showLoader: () => {},
  hideLoader: () => {},
});

export const ProgressProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // ── Barra de progreso top ─────────────────────────────────────────────────
  const [barVisible, setBarVisible] = useState(false);
  const [progress, setProgress]     = useState(0);
  const timerRef    = useRef<ReturnType<typeof setInterval> | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const start = useCallback((_label?: string) => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    if (timerRef.current)     clearInterval(timerRef.current);
    setProgress(0);
    setBarVisible(true);
    let p = 0;
    timerRef.current = setInterval(() => {
      p = p < 70 ? p + 8 : p < 90 ? p + 1 : p;
      setProgress(p);
    }, 120);
  }, []);

  const done = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    setProgress(100);
    hideTimerRef.current = setTimeout(() => {
      setBarVisible(false);
      setProgress(0);
    }, 400);
  }, []);

  // ── Overlay fullscreen ────────────────────────────────────────────────────
  const [loaderVisible, setLoaderVisible] = useState(false);
  const [loaderLabel,   setLoaderLabel]   = useState('Cargando...');

  const showLoader = useCallback((label = 'Cargando...') => {
    setLoaderLabel(label);
    setLoaderVisible(true);
  }, []);

  const hideLoader = useCallback(() => {
    setLoaderVisible(false);
  }, []);

  useEffect(() => () => {
    if (timerRef.current)    clearInterval(timerRef.current);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
  }, []);

  return (
    <ProgressContext.Provider value={{ start, done, showLoader, hideLoader }}>
      {children}

      {/* Barra top */}
      {barVisible && (
        <div className="fixed top-0 left-0 right-0 z-[9997] h-[3px] pointer-events-none">
          <div
            className="h-full bg-brand-yellow transition-all duration-300 ease-out rounded-r-full"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {/* Overlay fullscreen */}
      {loaderVisible && (
        <div className="fixed inset-0 z-[9996] bg-black/50 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-brand-surface rounded-2xl px-8 py-6 flex items-center gap-4 shadow-2xl border border-brand-border">
            {/* Spinner */}
            <div className="w-6 h-6 rounded-full border-[3px] border-brand-yellow border-t-transparent animate-spin flex-shrink-0" />
            <p className="text-brand-dark font-semibold text-sm">{loaderLabel}</p>
          </div>
        </div>
      )}
    </ProgressContext.Provider>
  );
};

export const useProgress = () => useContext(ProgressContext);
